import { Box, Link as MuiLink, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';

import {
  selectIsEditable,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  getCohortBuilderPath,
  getCohortsPath,
  getProjectsBasePath,
} from '@verantos-dev/core-constants';
import { useCohortProfileQuery } from '@verantos-dev/data-access-cohorts';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { AVDetailTitle } from '@verantos-dev/feature-analytical-variables';
import { Breadcrumbs } from '@verantos-dev/shared-components';

import { AVNameInlineEdit } from './AVNameInlineEdit';
import { useAVAutoSavePipeline } from './useAVAutoSavePipeline';
import { useAVNameEdit } from './useAVNameEdit';
import { useTriggerExitModal } from '../../containers/v0/cohort-builder-page-layout/CohortBuilderPageHeader/hooks/useTriggerExitModal';
import { useAVEditData } from '../../containers/v0/cohort-builder-page-tabs/AVEditPage/useAVEditData';
import { useResetCohortBuilderState } from '../../hooks/common/useResetCohortBuilderState';

interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface AVEditPageHeaderProps {
  /**
   * Suppress the in-page breadcrumb row. Set when the page is mounted
   * under a parent shell (gen_3 ``ProjectPage``) that already renders
   * a project-level breadcrumb — surfacing two breadcrumbs in the same
   * viewport is redundant and confusing.
   */
  hideBreadcrumbs?: boolean;
}

export const AVEditPageHeader = ({ hideBreadcrumbs = false }: AVEditPageHeaderProps = {}) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { projectId, cohortId, cohortVersionId, analyticalVariableId } = useParams();
  const { data: cohort } = useCohortProfileQuery({
    projectId: projectId || '',
    cohortId: cohortId || '',
  });
  const { data: projectData } = useGetProjectQuery(projectId || '', { skip: !projectId });
  const { analyticalVariable, conceptGroups, statusChips, hasError, hasWarning, errorCount } =
    useAVEditData(analyticalVariableId ?? '');
  const { onTriggerExitModal } = useTriggerExitModal();
  const { resetCohortBuilderState } = useResetCohortBuilderState();
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const handleAnalyticalVariableChange = useAVAutoSavePipeline({
    analyticalVariable,
    cohortId,
    conceptGroups,
  });
  const nameEdit = useAVNameEdit(analyticalVariable, handleAnalyticalVariableChange);
  const projectName = projectData?.data?.projectName || '';
  const cohortName = cohort?.cohortName || '';
  const variableName = analyticalVariable?.name || '';

  const cohortBuilderDefinitionPath = `${getCohortBuilderPath(
    projectId || '',
    cohortId || '',
    cohortVersionId || ''
  )}?pageTab=definition`;

  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: intl.formatMessage({
        id: 'projects.cohortBuilderPage.breadcrumbs.projects',
      }),
      href: getProjectsBasePath(),
      onClick: (e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        onTriggerExitModal(() => {
          navigate(getProjectsBasePath());
          resetCohortBuilderState();
        });
      },
    },
    {
      label: projectName,
      href: projectId ? getCohortsPath(projectId) : getProjectsBasePath(),
      onClick: (e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        onTriggerExitModal(() => {
          navigate(projectId ? getCohortsPath(projectId) : getProjectsBasePath());
          resetCohortBuilderState();
        });
      },
    },
    {
      label: intl.formatMessage({ id: 'projects.cohortBuilderPage.breadcrumbs.cohorts' }),
      href: projectId ? getCohortsPath(projectId) : getProjectsBasePath(),
      onClick: (e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        onTriggerExitModal(() => {
          navigate(projectId ? getCohortsPath(projectId) : getProjectsBasePath());
          resetCohortBuilderState();
        });
      },
    },
    {
      label: cohortName,
      href: cohortBuilderDefinitionPath,
      onClick: (e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        onTriggerExitModal(() => {
          navigate(cohortBuilderDefinitionPath);
        });
      },
    },
    {
      label: variableName,
    },
  ];

  return (
    <Box data-testid="av-edit-page-header" sx={{ py: theme.spacing(4), width: '100%' }}>
      {/* Breadcrumbs + Save button row */}
      {hideBreadcrumbs ? null : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Breadcrumbs
            id="av-edit-breadcrumbs"
            separator="/"
            sx={{ color: theme.palette.text.secondary, mb: theme.spacing(2) }}
          >
            {breadcrumbs.map(({ label, onClick, href }, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const sx = {
                fontWeight: '400',
                cursor: isLast ? 'default' : 'pointer',
                color: isLast ? theme.palette.text.primary : theme.palette.text.secondary,
                textDecoration: 'none',
              };

              if (href) {
                return (
                  <MuiLink
                    key={label}
                    component={RouterLink}
                    to={href}
                    underline={isLast ? 'none' : 'hover'}
                    typography="body1"
                    onClick={onClick}
                    data-testid={`av-edit-breadcrumb-${index + 1}`}
                    sx={sx}
                  >
                    {label}
                  </MuiLink>
                );
              }

              return (
                <MuiLink
                  key={label}
                  underline="none"
                  typography="body1"
                  data-testid={`av-edit-breadcrumb-${index + 1}`}
                  sx={sx}
                >
                  {label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}

      {/* Page title with role badges and inline name editing */}
      <Typography variant="h4" component="div" data-testid="av-edit-page-title">
        <AVNameInlineEdit
          isEditing={nameEdit.isEditing}
          editedName={nameEdit.editedName}
          validationError={nameEdit.validationError}
          isEditable={isEditable}
          onStartEdit={nameEdit.startEdit}
          onSave={nameEdit.saveEdit}
          onCancel={nameEdit.cancelEdit}
          onChange={nameEdit.handleNameChange}
        >
          <AVDetailTitle
            name={variableName}
            statusChips={statusChips}
            hasError={hasError}
            errorTooltip={
              hasError
                ? intl.formatMessage(
                    { id: 'projects.cohortBuilderPage.criteriaSection.avErrorTooltip' },
                    { count: errorCount }
                  )
                : undefined
            }
            hasWarning={hasWarning}
            warningTooltip={
              hasWarning
                ? intl.formatMessage({
                    id: 'projects.cohortBuilder.cohortBuilderItemViewCard.warningCriteria',
                  })
                : undefined
            }
          />
        </AVNameInlineEdit>
      </Typography>
    </Box>
  );
};
